import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../services/supabaseService';
import type { MaintenanceContract, MaintenanceTask, MaintenanceReport } from '../../types/maintenance';


const TASK_TYPE_LABELS: Record<string, string> = {
  bug_fix: 'Bug',
  improvement: 'Mejora',
  new_feature: 'Nueva func.',
  support: 'Soporte',
  security: 'Seguridad',
  server: 'Servidor',
};

export function MaintenancePortal() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contract, setContract] = useState<MaintenanceContract | null>(null);
  const [tasks, setTasks] = useState<MaintenanceTask[]>([]);
  const [reports, setReports] = useState<MaintenanceReport[]>([]);




  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    if (!token) {
      setError('Enlace no valido');
      setLoading(false);
      return;
    }

    try {
      // La RPC devuelve únicamente informes publicados y sus datos congelados.
      // Antes se hacían 4 SELECTs directos a maintenance_* que ahora están
      // bloqueados para anon por la migración 20260917190000.
      const { data, error } = await supabase.rpc('get_maintenance_portal_data', {
        p_token: token,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        setError('Contrato no encontrado');
        setLoading(false);
        return;
      }

      const c: MaintenanceContract = {
        id: row.contract_id,
        plan_name: row.plan_name,
        client_name: row.client_name,
        status: row.status,
        start_date: row.start_date,
        monthly_hours: row.monthly_hours,
        monthly_price: row.monthly_price,
        // Campos que la RPC no devuelve (support_hours, improvement_hours,
        // extra_hour_price) se completan con defaults para no romper el UI.
        support_hours: row.support_hours,
        improvement_hours: row.improvement_hours,
        extra_hour_price: 0,
      } as any;

      setContract(c);

      const reps = (row.reports || []) as MaintenanceReport[];
      setReports(reps);
      setTasks(reps.flatMap(r => r.tasks_snapshot || []));

    } catch (err) {
      console.error('Error loading portal data:', err);
      setError('Error cargando datos del contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (report: MaintenanceReport) => {
    if (!contract) return;
    const html2pdf = (await import('html2pdf.js')).default;

    const month = new Date(report.report_month).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

    const escape = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
    const snapshot = { ...contract, ...report.contract_snapshot };
    const content = `
      <div style="font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto;">
        <h1 style="color: #1a1a1a; margin-bottom: 4px;">Informe de Mantenimiento</h1>
        <p style="color: #666; font-size: 18px;">${escape(snapshot.client_name)} — ${month}</p>
        <p style="color: #999; font-size: 14px;">${escape(snapshot.plan_name)} | ${report.included_hours}h incluidas/mes</p>
        <hr style="margin: 24px 0; border-color: #eee;">
        <div style="display: flex; gap: 16px; margin-bottom: 32px;">
          <div style="flex:1; background: #eff6ff; padding: 16px; border-radius: 8px; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #1d4ed8;">${Number(report.total_hours_used).toFixed(2)}h</div>
            <div style="font-size: 14px; color: #3b82f6;">Total usadas</div>
          </div>
          <div style="flex:1; background: #f9fafb; padding: 16px; border-radius: 8px; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #374151;">${Number(report.support_hours_used).toFixed(2)}h</div>
            <div style="font-size: 14px; color: #6b7280;">Soporte</div>
          </div>
          <div style="flex:1; background: #faf5ff; padding: 16px; border-radius: 8px; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #7e22ce;">${Number(report.improvement_hours_used).toFixed(2)}h</div>
            <div style="font-size: 14px; color: #a855f7;">Mejoras</div>
          </div>
          <div style="flex:1; background: ${Number(report.extra_hours) > 0 ? '#fef2f2' : '#f0fdf4'}; padding: 16px; border-radius: 8px; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: ${Number(report.extra_hours) > 0 ? '#b91c1c' : '#15803d'};">
              ${Number(report.extra_hours) > 0 ? '+' + Number(report.extra_hours).toFixed(2) + 'h' : (report.included_hours - Number(report.total_hours_used)).toFixed(2) + 'h'}
            </div>
            <div style="font-size: 14px; color: ${Number(report.extra_hours) > 0 ? '#ef4444' : '#22c55e'};">
              ${Number(report.extra_hours) > 0 ? 'Extra (' + Number(report.extra_hours_cost).toFixed(0) + '\u20AC)' : 'Caducadas'}
            </div>
          </div>
        </div>
        ${report.summary ? `<div style="margin-bottom: 32px;"><h3 style="color: #1a1a1a;">Resumen</h3><p style="color: #444; line-height: 1.6;">${escape(report.summary)}</p></div>` : ''}
        ${(report.checklist_snapshot || []).length ? `<h3>Checklist mensual</h3><ul>${(report.checklist_snapshot || []).map(item => `<li>${item.completed_at ? 'Completada' : 'Pendiente'} · ${escape(item.title)}${item.completed_at ? ` · ${escape(item.completed_by_name)} · ${escape(new Date(item.completed_at).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' }))}` : ''}</li>`).join('')}</ul>` : ''}
        <table>${(report.tasks_snapshot || []).map(t => `<tr><td>${escape(t.task_date)}</td><td>${escape(t.title)}</td><td>${Number(t.hours).toFixed(2)} h</td></tr>`).join('')}</table>
        <p>Las horas no utilizadas caducan al cierre y no se acumulan.</p>
        <p style="color: #999; font-size: 12px; margin-top: 40px; text-align: center;">Cero Ideas — Informe generado automaticamente</p>
      </div>
    `;

    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `informe-${contract.client_name}-${report.report_month.slice(0, 7)}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(content)
      .save();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md text-center">
          <p className="text-red-600 text-lg font-medium">{error || 'Error desconocido'}</p>
          <p className="text-gray-500 mt-2">Si crees que esto es un error, contacta con Cero Ideas.</p>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Portal de Mantenimiento</h1>
              <p className="text-sm text-gray-500">Cero Ideas</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900">{contract.client_name}</p>
              <p className="text-sm text-gray-500">{contract.plan_name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Plan info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Tu plan</h2>
          <div className="grid grid-cols-3 gap-4 text-sm mb-4">
            <div>
              <span className="text-gray-500">Horas incluidas</span>
              <p className="font-semibold text-lg">{contract.monthly_hours}h/mes</p>
            </div>
            <div>
              <span className="text-gray-500">Soporte</span>
              <p className="font-semibold">{contract.support_hours}h</p>
            </div>
            <div>
              <span className="text-gray-500">Mejoras</span>
              <p className="font-semibold">{contract.improvement_hours}h</p>
            </div>
          </div>
        </div>

        <p className="bg-blue-50 p-4 rounded-lg text-blue-900">Informes publicados de mantenimiento. Las horas no utilizadas caducan al finalizar cada mes y no se acumulan. Comunica tus solicitudes por el grupo habitual de WhatsApp.</p>

        {/* Tasks this month */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Intervenciones publicadas</h2>
          {tasks.length === 0 ? (
            <p className="text-gray-500 text-sm">No hay intervenciones publicadas</p>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div key={task.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{task.title}</p>
                    {task.description && (
                      <p className="text-sm text-gray-500 mt-0.5">{task.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-400">{task.task_date}</span>
                      <span className="text-xs text-gray-400">{TASK_TYPE_LABELS[task.task_type]}</span>
                    </div>
                  </div>
                  <span className="text-sm font-mono font-semibold text-gray-700">{Number(task.hours).toFixed(2)}h</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Historical reports */}
        {reports.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informes mensuales</h2>
            <div className="space-y-2">
              {reports.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(r.report_month).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Number(r.total_hours_used).toFixed(2)}h usadas de {r.included_hours}h
                    </p>
                  </div>
                  <button
                    onClick={() => handleDownloadPdf(r)}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Descargar PDF
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 py-4">
          Cero Ideas — Portal de mantenimiento
        </div>
      </main>
    </div>
  );
}

export default MaintenancePortal;
